/**
 * TechDayList (BOOK-A-CALL-REDESIGN, Seth 2026-10-21 "Proceed") — the chosen
 * tech's day, top to bottom: time · who · what · where · how long, one line
 * per call, nothing cut off. The new visit is the dashed row; drag it up or
 * down to set where it runs (its promised WINDOW is its own control on the
 * row — order and window are independent, as on the old sideways lane:
 * SCHED-MINIBOARD slice 4). Replaces ScheduleMiniBoard inside Book a Call;
 * the lane could not hold seven named stops across.
 */
import { useRef, useState, type ReactNode } from "react";
import { GripVertical } from "lucide-react";
import { cn } from "@/lib/cn";
import { windowColors } from "@/lib/window";
import type { TimeSuggestion, LaneStop } from "@/features/board/suggestTimes";

const WIN_HOUR: Record<string, number> = { "8AM": 8, "8-12": 9, "10-2": 11, "12-4": 13, "3-7": 16 };
const DAY_LONG: Record<string, string> = { Sun: "Sunday", Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday", Fri: "Friday", Sat: "Saturday" };
const ordinal = (n: number) => `${n}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;
function clock(h: number): string {
  let hr = Math.floor(h);
  let m = Math.round((h - hr) * 60);
  if (m === 60) { hr += 1; m = 0; }
  return `${hr % 12 || 12}:${String(m).padStart(2, "0")}${hr >= 12 ? "p" : "a"}`;
}
const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join("");

export function TechDayList({
  suggestion: s, chosenWindow, onWindowChange, exactHour = null, onCommitTime, preview, windows, windowLabel, extra,
}: {
  suggestion: TimeSuggestion;
  chosenWindow: string;
  onWindowChange: (w: string) => void;
  exactHour?: number | null;
  onCommitTime?: (s: TimeSuggestion, hour: number) => void;
  /** The new visit as the board will draw it. */
  preview: { name: string; type?: string | null; plus?: boolean; asap?: boolean; city?: string | null; minutes?: number | null };
  windows: string[];
  windowLabel: (w: string) => string;
  /** A line under the header (the after-hours note, a fit warning). */
  extra?: ReactNode;
}) {
  const stops = s.stops;
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [dragPos, setDragPos] = useState<number | null>(null);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const posRef = useRef<number | null>(null);

  const posForHour = (h: number) => stops.filter((st) => st.hour < h).length;
  const timeForPos = (pos: number): number => {
    const b = stops[pos - 1];
    const a = stops[pos];
    if (b && a) return (b.hour + a.hour) / 2;
    if (b) return Math.min(18.5, b.hour + 1);
    if (a) return Math.max(7.5, a.hour - 1);
    return 9;
  };
  // Place the new row to MATCH its promised window so the pill and the position
  // agree; a drag (exactHour) wins; Anytime falls back to the copilot's slot.
  const placeHour = exactHour ?? (WIN_HOUR[chosenWindow] ?? s.slotHour);
  const basePos = posForHour(placeHour);
  const pos = dragPos ?? basePos;
  const effWin = chosenWindow === "Anytime" && s.window ? s.window : chosenWindow;
  const wc = windowColors(effWin);
  const cities = stops.map((st) => st.city).filter(Boolean) as string[];
  const route = cities.length ? `${cities[0]}${cities.length > 1 ? ` → ${cities[cities.length - 1]}` : ""}` : null;
  const dayShort = s.dayLabel.split(/\s|,/)[0];
  const dayName = DAY_LONG[dayShort] ?? dayShort;
  const possessive = `${s.techName.split(/\s+/)[0]}'s`;

  const items: ({ kind: "stop"; st: LaneStop; i: number } | { kind: "new" })[] = [
    ...stops.slice(0, pos).map((st, i) => ({ kind: "stop" as const, st, i })),
    { kind: "new" as const },
    ...stops.slice(pos).map((st, k) => ({ kind: "stop" as const, st, i: pos + k })),
  ];

  // One exit for the drag, however it ends: commit the drop once, then clear.
  const endDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    if (movedRef.current && onCommitTime) onCommitTime(s, timeForPos(posRef.current ?? basePos));
    setDragPos(null);
    posRef.current = null;
  };
  const posFromY = (y: number) => {
    // The new row lands before the first stop whose middle is below the pointer.
    let p = 0;
    stops.forEach((_, i) => {
      const el = rowRefs.current[i];
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (y > r.top + r.height / 2) p = i + 1;
    });
    return p;
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-hairline bg-white">
      <div className="flex items-center gap-2.5 border-b border-slate-100 bg-slate-50/70 px-3.5 py-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-800 text-[11px] font-extrabold text-white">{initials(s.techName)}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[14px] font-extrabold tracking-tight text-slate-900">{possessive} {dayName}</span>
            <span className="text-[12px] text-slate-500">{s.dayLabel.replace(/^\w+,?\s*/, "")} · {stops.length} call{stops.length === 1 ? "" : "s"}{route ? ` · ${route}` : ""}</span>
          </div>
          {extra && <div className="mt-0.5 text-[12px] text-slate-600">{extra}</div>}
        </div>
        <span className="shrink-0 text-[12px] font-semibold text-slate-500">{s.addedMiles < 0.5 ? "no extra miles" : `+${Math.round(s.addedMiles)} mi`}</span>
      </div>

      <div ref={listRef} className="divide-y divide-slate-100">
        {items.map((it) => {
          if (it.kind === "new") {
            return (
              <div
                key="new"
                role="slider"
                aria-label="Drag to set the run order"
                aria-valuetext={`runs ${ordinal(pos + 1)}, promised ${effWin}`}
                tabIndex={0}
                style={{ touchAction: "none" }}
                onPointerDown={(e) => {
                  if ((e.target as HTMLElement).closest("select")) return;
                  e.stopPropagation();
                  try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* synthetic */ }
                  draggingRef.current = true;
                  movedRef.current = false;
                  setDragPos(basePos);
                  // The drop lands even when the pointer is released off the row
                  // (or capture is lost) — a window-level backstop.
                  const finish = () => { window.removeEventListener("pointerup", finish); window.removeEventListener("pointercancel", finish); endDrag(); };
                  window.addEventListener("pointerup", finish);
                  window.addEventListener("pointercancel", finish);
                }}
                onPointerMove={(e) => {
                  if (!draggingRef.current) return;
                  movedRef.current = true;
                  const p = posFromY(e.clientY);
                  posRef.current = p;
                  setDragPos(p);
                }}
                onPointerUp={(e) => {
                  try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* synthetic */ }
                  endDrag();
                }}
                onLostPointerCapture={() => endDrag()}
                onKeyDown={(e) => {
                  if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
                  e.preventDefault();
                  const np = Math.max(0, Math.min(stops.length, basePos + (e.key === "ArrowDown" ? 1 : -1)));
                  onCommitTime?.(s, timeForPos(np));
                }}
                className={cn(
                  "mx-2 my-1 grid cursor-grab select-none grid-cols-[56px_14px_minmax(0,1fr)_auto] items-center gap-x-3 rounded-xl border-2 border-dashed border-brand-500 bg-brand-50/60 px-3 py-2.5",
                  dragPos != null && "cursor-grabbing ring-2 ring-brand-300",
                )}
                title={`Runs ${ordinal(pos + 1)} in the route — drag up or down to move it; the window is the control on the right`}
              >
                <span className="text-right text-[12px] font-bold text-brand-700">{ordinal(pos + 1)}</span>
                <span className="h-2.5 w-2.5 justify-self-center rounded-full bg-brand-600 ring-4 ring-brand-100" />
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                    <GripVertical className="h-3.5 w-3.5 shrink-0 text-brand-500" />
                    <span className="truncate text-[14px] font-bold text-slate-900">{preview.name}</span>
                    {effWin && effWin !== "Anytime" && <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider", wc.bg, wc.text)}>{effWin}</span>}
                    {preview.plus && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 ring-1 ring-amber-300/70">Plus</span>}
                    {preview.asap && <span className="rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">ASAP</span>}
                  </div>
                  {preview.type && <div className="truncate text-[12px] text-slate-600">{preview.type}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={effWin}
                    onChange={(e) => onWindowChange(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    aria-label="Promised arrival window"
                    className="h-7 rounded-full border border-brand-300 bg-white px-2 text-[12px] font-bold text-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-400/40"
                  >
                    {windows.map((w) => <option key={w} value={w}>{w === "Anytime" ? "Anytime" : windowLabel(w)}</option>)}
                    {!windows.includes(effWin) && <option value={effWin}>{effWin}</option>}
                  </select>
                  <span className="whitespace-nowrap text-[12px] text-slate-400">{[preview.city, preview.minutes ? `${preview.minutes}m` : null].filter(Boolean).join(" · ")}</span>
                </div>
              </div>
            );
          }
          const st = it.st;
          const c = windowColors(st.tag ?? st.window);
          const where = [st.city, st.minutes ? `${st.minutes}m` : null].filter(Boolean).join(" · ");
          return (
            <div
              key={`s${it.i}`}
              ref={(el) => { rowRefs.current[it.i] = el; }}
              title={[st.name, st.what, st.address, st.window ? `promised ${windowLabel(st.window)}` : clock(st.hour), st.tag, st.status].filter(Boolean).join(" · ")}
              className="grid grid-cols-[56px_14px_minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-2.5"
            >
              <span className="text-right text-[12px] font-semibold tabular-nums text-slate-500">{st.window ?? clock(st.hour)}</span>
              <span className={cn("h-2.5 w-2.5 justify-self-center rounded-full", c.bg, "ring-1 ring-black/10")} />
              <div className="min-w-0">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-[14px] font-bold text-slate-900">{st.name ?? st.city ?? "Stop"}</span>
                  {st.tag && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700">{st.tag}</span>}
                  {/* The exception wears the pill: an ESC call on a Pegasus day (Seth 2026-10-21). */}
                  {!st.pegasus && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500" title="Booked in ESC">ESC</span>}
                </div>
                {st.what && <div className="truncate text-[12.5px] text-slate-500">{st.what}</div>}
              </div>
              <span className="whitespace-nowrap text-[12px] text-slate-400">{where}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
